import {
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import {BaseControlDirective} from '../../directives/base-control.directive';

@Component({
  selector: 'app-options-list',
  templateUrl: './options-list.component.html',
  styleUrls: ['./options-list.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => OptionsListComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => OptionsListComponent), multi: true},
  ]
})
export class OptionsListComponent extends BaseControlDirective implements ControlValueAccessor, OnInit {
  @Input() hasCustomTemplate: boolean;
  @Input() multiple: boolean;
  @Input() form: UntypedFormGroup;
  @Input() controlName: string;
  @Input() bordered: boolean;
  @Input() isHorizontal: boolean;
  @Input() allowArrowNavigation = true;
  @Input() secondaryProp = 'description';
  @Input() disableWithIds = [];

  @Input() set optionsList(options: any) {
    this.options = options || [];
  }

  get optionsList(): any {
    return this.options;
  }

  @Input() borderedNoBackground: boolean;

  @Output() optionSelected: EventEmitter<any> = new EventEmitter();
  @Output() keydownTab: EventEmitter<void> = new EventEmitter();
  @Output() linkClicked: EventEmitter<any> = new EventEmitter();

  @ContentChild(TemplateRef) templateRef: TemplateRef<any>;
  @ViewChild('optionsWrapper') optionsWrapper;
  @ViewChildren('optionsList') optionsQueryList: QueryList<ElementRef>;

  set value(v: any) {
    this.innerValue = v;
    super.writeValue(v);
    this.onChangeCallback(this.innerValue);
  }

  get value(): any {
    return this.innerValue;
  }

  options: any = [];
  multipleValue: any[] = [];
  focusedOptionIndex = -1;

  @HostListener('keydown', ['$event'])
  keyDown(ev): void {
    if (ev.key === 'ArrowUp') {
      this.arrowNavigationHandler(-1);
    } else if (ev.key === 'ArrowDown') {
      this.arrowNavigationHandler(+1);
    } else if (ev.key === 'Enter' || (ev.key === 'Tab' && this.hasCustomTemplate) || ev.key === ' ') {
      setTimeout(() => {
        if (this.focusedOptionIndex >= 0 && this.focusedOptionIndex < this.options.length) {
          this.multiple ? this.toggleValue(this.focusedOptionIndex) : this.selectOption(this.options[this.focusedOptionIndex]);
        }
      }, 100);
    } else if (ev.key === 'Tab' && !this.hasCustomTemplate) {
      this.keydownTab.emit();
    }

    ev.stopPropagation();
  }

  constructor(private fb: UntypedFormBuilder) {
    super();
  }

  ngOnInit(): void {
    if (this.multiple) {
      this.multipleValue = [...this.form.get(this.controlName)?.value];
      this.options.forEach((el: any) => {
        el.checked = this.multipleValue.includes(el.id);
      });
    }
  }

  // works for formGroup array only
  toggleValue(i: number): void {
    if (!this.options[i].checked) {
      this.options[i].checked = true;
      this.multipleValue.push(this.options[i].id);
    } else {
      this.options[i].checked = false;
      const idx = this.multipleValue.findIndex((el: any) => el === this.options[i].id);
      this.multipleValue.splice(idx, 1);
    }

    this.form.setControl(this.controlName, this.fb.array(this.multipleValue || []));
    this.optionSelected.emit();
  }

  selectOption(option: any): void {
    if (this.isDisabled(option.id))
      return;

    this.value = option.id;
    this.optionSelected.emit(option);
  }

  isOverflown(element: Element): boolean {
    return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
  }

  arrowNavigationHandler(increment: number): void {
    if (this.optionsQueryList && this.allowArrowNavigation) {
      const queryList = this.optionsQueryList.toArray();

      if (queryList?.length) {
        this.focusedOptionIndex += increment;

        if (this.focusedOptionIndex < -1 || this.focusedOptionIndex === queryList.length) {
          this.focusedOptionIndex = 0;
        } else if (this.focusedOptionIndex === -1) {
          this.focusedOptionIndex = queryList.length - 1;
        }

        setTimeout(() => queryList[this.focusedOptionIndex].nativeElement.focus());
      }
    }
  }

  isDisabled(id): boolean { 
   return this.disableWithIds && this.disableWithIds.includes(id);
  }
}
